import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDocTitle } from '../hooks/useDocTitle';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import Footer from '../sections/Footer';

const ENGINE_GREEN = '#00FF88';

const cardGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0,255,136,0.08); }
  50%       { box-shadow: 0 0 40px rgba(0,255,136,0.22); }
`;
const orbFloat = keyframes`
  0%, 100% { transform: translate(-50%,-50%) scale(1); opacity: 0.7; }
  50%       { transform: translate(-50%,-50%) scale(1.1); opacity: 1; }
`;
const gridScroll = keyframes`
  0%   { transform: translateY(0); }
  100% { transform: translateY(64px); }
`;

/* ── Hero ── */
const Hero = styled.section`
  position: relative;
  padding: 180px 80px 120px;
  overflow: hidden;
  @media (max-width: 900px) { padding: 140px 24px 80px; }
`;

const HeroGrid = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0,255,136,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,255,136,0.03) 1px, transparent 1px);
  background-size: 64px 64px;
  animation: ${gridScroll} 22s linear infinite;
`;

const HeroGlow = styled.div`
  position: absolute;
  width: 700px; height: 700px;
  border-radius: 50%;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(0,255,136,0.1) 0%, transparent 65%);
  animation: ${orbFloat} 5s ease-in-out infinite;
  pointer-events: none;
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const BetaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(0,255,136,0.35);
  padding: 6px 16px;
  margin-bottom: 32px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10px;
  letter-spacing: 0.28em;
  color: ${ENGINE_GREEN};
  text-transform: uppercase;

  &::before {
    content: '';
    width: 6px; height: 6px;
    background: ${ENGINE_GREEN};
    border-radius: 50%;
    display: inline-block;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 9vw, 120px);
  line-height: 0.88;
  letter-spacing: 0.02em;
  margin-bottom: 28px;

  .accent {
    color: ${ENGINE_GREEN};
    text-shadow: 0 0 60px rgba(0,255,136,0.4);
    display: block;
  }
`;

const HeroDesc = styled.p`
  font-size: 17px;
  line-height: 1.82;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 680px;
  margin: 0 auto 48px;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ButtonGreen = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 15px 38px;
  clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%);
  transition: all 0.3s ease;
  background: linear-gradient(135deg, ${ENGINE_GREEN}, #00CC66);
  color: #000;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 0 40px rgba(0,255,136,0.4);
  }

  @media (max-width: 480px) { padding: 13px 28px; font-size: 12px; }
`;

const ButtonOutline = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 15px 38px;
  clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%);
  transition: all 0.3s ease;
  background: transparent;
  color: rgba(255,255,255,0.85);
  border: 1px solid rgba(255,255,255,0.18);

  &:hover {
    border-color: ${ENGINE_GREEN};
    color: ${ENGINE_GREEN};
    box-shadow: 0 0 24px rgba(0,255,136,0.18);
    background: rgba(0,255,136,0.05);
  }

  @media (max-width: 480px) { padding: 13px 28px; font-size: 12px; }
`;

/* ── Features ── */
const FeaturesSection = styled.section`
  padding: 120px 80px;
  @media (max-width: 900px) { padding: 80px 24px; }
`;

const FeaturesInner = styled.div`
  max-width: 1360px;
  margin: 0 auto;
`;

const FeaturesHeader = styled.div`
  text-align: center;
  margin-bottom: 72px;
`;

const FeaturesTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(48px, 7vw, 90px);
  line-height: 0.9;
  letter-spacing: 0.02em;
  margin-bottom: 16px;
  color: ${ENGINE_GREEN};
  text-shadow: 0 0 50px rgba(0,255,136,0.3);
`;

const FeaturesSubtitle = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textMuted};
  letter-spacing: 0.06em;
`;

const FeatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid rgba(0,255,136,0.1);
  padding: 36px 30px;
  transition: border-color 0.4s, transform 0.4s;

  &:hover {
    border-color: rgba(0,255,136,0.4);
    transform: translateY(-5px);
    animation: ${cardGlow} 2s ease-in-out infinite;
  }
`;

const FeatureIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  filter: drop-shadow(0 0 16px rgba(0,255,136,0.4));
`;

const FeatureTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 14px;
  letter-spacing: 0.14em;
  color: ${ENGINE_GREEN};
  margin-bottom: 12px;
  text-transform: uppercase;
`;

const FeatureDesc = styled.p`
  font-size: 14px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ── Download CTA ── */
const DownloadSection = styled.section`
  padding: 120px 80px;
  background: ${({ theme }) => theme.colors.mid};
  text-align: center;
  position: relative;
  overflow: hidden;
  @media (max-width: 900px) { padding: 80px 24px; }
`;

const DownloadGlow = styled.div`
  position: absolute;
  width: 600px; height: 600px;
  border-radius: 50%;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(0,255,136,0.08) 0%, transparent 70%);
  pointer-events: none;
`;

const DownloadInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 680px;
  margin: 0 auto;
`;

const DownloadTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(48px, 8vw, 100px);
  line-height: 0.9;
  letter-spacing: 0.02em;
  margin-bottom: 20px;
  color: ${ENGINE_GREEN};
  text-shadow: 0 0 60px rgba(0,255,136,0.35);
`;

const DownloadDesc = styled.p`
  font-size: 16px;
  line-height: 1.78;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 48px;
`;

const DownloadActions = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

export default function EnginePage() {
  const { t } = useTranslation();
  const featuresData = t('engineShowcase.features', { returnObjects: true });

  useDocTitle('pageTitles.engine');

  return (
    <>
      <Hero>
        <HeroGrid />
        <HeroGlow />
        <HeroInner>
          <BetaBadge>{t('enginePage.betaBadge')}</BetaBadge>
          <HeroTitle>
            {t('enginePage.titleLine1')}<br />
            <span className="accent">{t('enginePage.titleAccent')}</span>
          </HeroTitle>
          <HeroDesc>{t('enginePage.desc')}</HeroDesc>
          <HeroActions>
            <ButtonGreen
              href="https://github.com/GueblyGames/guebly-engine/releases/latest"
              target="_blank"
              rel="noopener noreferrer"
            >
              ⬇ &nbsp;{t('enginePage.btnDownload')}
            </ButtonGreen>
            <ButtonOutline
              href="https://docs.games.guebly.com.br"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('enginePage.btnDocs')}
            </ButtonOutline>
            <ButtonOutline
              href="https://discord.gg/gueblygames"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('enginePage.btnCommunity')}
            </ButtonOutline>
          </HeroActions>
        </HeroInner>
      </Hero>

      <FeaturesSection>
        <FeaturesInner>
          <AnimatedSection>
            <FeaturesHeader>
              <SectionLabel>{t('enginePage.sectionLabel')}</SectionLabel>
              <FeaturesTitle>{t('enginePage.featuresTitle')}</FeaturesTitle>
              <FeaturesSubtitle>{t('enginePage.featuresSubtitle')}</FeaturesSubtitle>
            </FeaturesHeader>
          </AnimatedSection>

          <FeatGrid>
            {featuresData.map((f, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <FeatureCard>
                  <FeatureIcon>{f.icon}</FeatureIcon>
                  <FeatureTitle>{f.title}</FeatureTitle>
                  <FeatureDesc>{f.desc}</FeatureDesc>
                </FeatureCard>
              </AnimatedSection>
            ))}
          </FeatGrid>
        </FeaturesInner>
      </FeaturesSection>

      <DownloadSection>
        <DownloadGlow />
        <AnimatedSection>
          <DownloadInner>
            <SectionLabel>{t('enginePage.sectionLabel')}</SectionLabel>
            <DownloadTitle>{t('enginePage.downloadTitle')}</DownloadTitle>
            <DownloadDesc>{t('enginePage.downloadDesc')}</DownloadDesc>
            <DownloadActions>
              <ButtonGreen
                href="https://github.com/GueblyGames/guebly-engine/releases/latest"
                target="_blank"
                rel="noopener noreferrer"
              >
                ⬇ &nbsp;{t('enginePage.btnDownload')}
              </ButtonGreen>
              <ButtonOutline
                href="https://docs.games.guebly.com.br"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('enginePage.btnDocs')}
              </ButtonOutline>
            </DownloadActions>
          </DownloadInner>
        </AnimatedSection>
      </DownloadSection>

      <Footer />
    </>
  );
}
